import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placeTip, tipLabel } from './autoTooltipModel';

const OPEN_MS = 450;

/**
 * One tooltip for every icon-only control in the app (owner, 2026-10-07: "when
 * I hover it, a small unobtrusive box that names the action"). Mounted once in
 * the shell; listens on the document, so no call site has to change.
 *
 * A control qualifies when it is a button / link / [role=button] with no
 * visible text and an accessible name (`aria-label`, `title` or sr-only text).
 * Mouse hover after a short delay, or keyboard focus (`:focus-visible`); hidden
 * on leave, scroll, Escape or click. Visual only (`aria-hidden`): the control
 * keeps its own accessible name. Wrappers that already show a tooltip opt out
 * with `data-tooltip-owner` (ui/Tooltip); any element can opt out with
 * `data-no-tooltip`, or opt in with a custom text via `data-tip`.
 */
export default function AutoTooltip() {
  const [tip, setTip] = useState<{ text: string; top: number; left: number; below: boolean } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const current = useRef<HTMLElement | null>(null);
  const savedTitle = useRef<string | null>(null);

  useEffect(() => {
    const restoreTitle = () => {
      if (current.current && savedTitle.current != null) current.current.setAttribute('title', savedTitle.current);
      savedTitle.current = null;
    };
    const hide = () => {
      window.clearTimeout(timer.current);
      restoreTitle();
      current.current = null;
      setTip(null);
    };
    const show = (el: HTMLElement, delay: number) => {
      const text = tipLabel(el);
      if (!text) return;
      window.clearTimeout(timer.current);
      current.current = el;
      // the native title would show a second, slower tooltip: park it while ours is up
      if (el.hasAttribute('title')) { savedTitle.current = el.getAttribute('title'); el.removeAttribute('title'); }
      timer.current = window.setTimeout(() => {
        if (current.current !== el || !el.isConnected) return;
        const r = el.getBoundingClientRect();
        setTip({ text, ...placeTip(r, window.innerWidth) });
      }, delay);
    };
    const controlOf = (t: EventTarget | null) =>
      (t instanceof Element ? t.closest<HTMLElement>('button, a[href], [role="button"]') : null);

    const onOver = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const el = controlOf(e.target);
      if (el === current.current) return;
      hide();
      if (el) show(el, OPEN_MS);
    };
    const onOut = (e: PointerEvent) => {
      if (!current.current) return;
      const to = e.relatedTarget instanceof Node ? e.relatedTarget : null;
      if (to && current.current.contains(to)) return;
      hide();
    };
    const onFocusIn = (e: FocusEvent) => {
      const el = controlOf(e.target);
      hide();
      if (el && el.matches(':focus-visible')) show(el, 0);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };

    document.addEventListener('pointerover', onOver);
    document.addEventListener('pointerout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', hide);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      hide();
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('pointerout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, []);

  if (!tip) return null;
  return createPortal(
    <div
      aria-hidden="true"
      className={`auto-tip pointer-events-none fixed z-toast max-w-xs -translate-x-1/2 rounded-md bg-inverse px-2 py-1 text-xs font-medium text-on-inverse shadow-elevated ${tip.below ? '' : '-translate-y-full'}`}
      style={{ top: tip.top, left: tip.left }}
    >
      {tip.text}
    </div>,
    document.body,
  );
}
